import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { DialogService } from '../../../domain/services/dialog/dialog.service';
import { TaxRate, TaxRatesService } from '../../../domain/services/admin/tax-rates.service';

/**
 * County TDT tax-rate admin. Edits the same tax_rate table the generation engine reads,
 * so changing a county's rate is data — no code change.
 */
@Component({
  selector: 'app-tax-rates',
  standalone: true,
  imports: [
    CommonModule, FormsModule, MatCardModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatTableModule, MatProgressBarModule,
  ],
  templateUrl: './tax-rates.component.html',
  styleUrl: './tax-rates.component.css',
})
export class TaxRatesComponent implements OnInit {
  rates: TaxRate[] = [];
  loading = false;
  busy = false;
  error: string | null = null;

  readonly columns = ['county', 'rate', 'effectiveFrom', 'effectiveTo', 'actions'];

  // Add form
  draft: TaxRate = this.blank();

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private readonly service: TaxRatesService,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/app/admin/tax-rates', label: 'Administrative' },
      { icon: 'percent', url: '/app/admin/tax-rates', label: 'Tax rates' },
    ]);
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.service.getAll().subscribe({
      next: (r) => { this.rates = r; this.loading = false; },
      error: () => { this.error = 'Failed to load tax rates.'; this.loading = false; },
    });
  }

  add(): void {
    if (!this.draft.county || this.draft.rate == null || !this.draft.effectiveFrom) {
      this.error = 'County, rate and effective-from are required.';
      return;
    }
    this.busy = true; this.error = null;
    this.service.create({ ...this.draft, effectiveTo: this.draft.effectiveTo || null }).subscribe({
      next: () => { this.busy = false; this.draft = this.blank(); this.load(); },
      error: (e) => { this.busy = false; this.error = e?.error?.message || 'Failed to add rate.'; },
    });
  }

  remove(r: TaxRate): void {
    this.dialogService.openDeletedDialog(
      'Delete tax rate',
      `Delete the ${this.pct(r.rate)} rate for ${r.county} effective ${r.effectiveFrom}?`,
      (confirmed: boolean) => {
        if (confirmed && r.id) {
          this.service.delete(r.id).subscribe({ next: () => this.load(), error: () => { this.error = 'Delete failed.'; } });
        }
      },
    );
  }

  pct(rate: number): string {
    return rate != null ? `${(rate * 100).toFixed(2)}%` : '';
  }

  private blank(): TaxRate {
    return { county: '', rate: 0, effectiveFrom: '', effectiveTo: null };
  }
}
